import { useMemo } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import type {
  CommentCategory,
  CommentDashboard as CommentDashboardData,
  CommentDashboardDay,
} from "~/ui-rtk/api/commentModerationApi"
import { SERIES_COLORS } from "~/ui-rtk/pages/BudgetRecommender/components/HistoryChart"

/**
 * Fixed colour per series, taken from the app's validated six-slot chart
 * palette (HistoryChart.SERIES_COLORS, checked against the dark surface):
 * the three hide categories, "other hidden", and kept. This stacking order
 * (pink, blue, orange-red, violet, green) passes the dataviz palette
 * validator's adjacent-pair CVD and normal-vision checks on #101028; amber
 * next to orange-red did not. Same slot everywhere on this dashboard, so a
 * category keeps its colour whatever is filtered.
 */
const SERIES: Array<{
  key: keyof Omit<CommentDashboardDay, "date">
  label: string
  color: string
}> = [
  { key: "competitor", label: "Competitor", color: SERIES_COLORS[4] },
  { key: "spam", label: "Spam", color: SERIES_COLORS[0] },
  { key: "negative", label: "Negative", color: SERIES_COLORS[1] },
  { key: "otherHidden", label: "Other hidden", color: SERIES_COLORS[5] },
  { key: "kept", label: "Kept", color: SERIES_COLORS[2] },
]

const CATEGORY_COLOR: Record<CommentCategory, string> = {
  COMPETITOR: SERIES_COLORS[4],
  SPAM: SERIES_COLORS[0],
  NEGATIVE: SERIES_COLORS[1],
  OTHER: SERIES_COLORS[5],
  QUESTION: SERIES_COLORS[3],
  PRAISE: SERIES_COLORS[2],
  NEUTRAL: "#677489",
}

const JUDGE_LABEL = { RULE: "rule", AI: "AI", USER: "person" } as const

const CATEGORY_LABEL: Record<CommentCategory, string> = {
  COMPETITOR: "Competitor",
  SPAM: "Spam",
  NEGATIVE: "Negative",
  QUESTION: "Question",
  PRAISE: "Praise",
  NEUTRAL: "Neutral",
  OTHER: "Other",
}

const GRID = "#222446"
const MUTED = "#677489"
const INK = "#F8FAFC"

function shortDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  })
}

/** Fill the gaps so a quiet day shows as zero instead of vanishing from the axis. */
function fillDays(days: CommentDashboardDay[]): CommentDashboardDay[] {
  if (days.length < 2) return days
  const byDate = new Map(days.map(d => [d.date, d]))
  const out: CommentDashboardDay[] = []
  const cursor = new Date(`${days[0].date}T00:00:00Z`)
  const end = new Date(`${days[days.length - 1].date}T00:00:00Z`)
  while (cursor <= end) {
    const date = cursor.toISOString().slice(0, 10)
    out.push(
      byDate.get(date) ?? {
        date,
        competitor: 0,
        spam: 0,
        negative: 0,
        otherHidden: 0,
        kept: 0,
      },
    )
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return out
}

function Panel({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col rounded-lg border border-white/10 bg-white/5">
      <div className="flex items-baseline justify-between border-b border-white/10 px-4 py-2">
        <h2 className="text-sm font-medium text-white">{title}</h2>
        {hint && (
          <span className="text-[11px] text-system-gray-500">{hint}</span>
        )}
      </div>
      <div className="flex-1 px-4 py-3">{children}</div>
    </section>
  )
}

/** A horizontal bar row: label, a thin bar scaled to `max`, the value. */
function BarRow({
  label,
  value,
  max,
  color,
  sub,
  href,
}: {
  label: string
  value: number
  max: number
  color: string
  sub?: string
  href?: string
}) {
  const width = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0
  return (
    <li className="flex items-center gap-3 text-xs">
      <div
        className="w-40 shrink-0 truncate text-system-gray-200"
        title={label}
      >
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="underline decoration-white/20 hover:text-white"
          >
            {label}
          </a>
        ) : (
          label
        )}
        {sub && (
          <div className="truncate text-[10px] text-system-gray-500">{sub}</div>
        )}
      </div>
      <div className="h-2 flex-1 rounded-full bg-white/5">
        <div
          className="h-2 rounded-full"
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
      <div className="w-10 shrink-0 text-right font-mono text-system-gray-100">
        {value}
      </div>
    </li>
  )
}

function Empty({ text }: { text: string }) {
  return <p className="py-6 text-center text-xs text-system-gray-500">{text}</p>
}

/**
 * The comments dashboard: what the moderation saw and did in the window.
 * Trend (judged per day, stacked by why), category mix, platform and judge
 * split, the competitor / spam terms that fired, the posts drawing the most
 * hidden comments, and the authors who keep coming back.
 */
export default function CommentDashboard({
  data,
  isLoading,
}: {
  data: CommentDashboardData | undefined
  isLoading: boolean
}) {
  const days = useMemo(() => fillDays(data?.daily ?? []), [data])
  if (isLoading && !data) {
    return <p className="text-sm text-system-gray-400">Loading dashboard…</p>
  }
  if (!data || data.totals.total === 0) {
    return null
  }
  const categories = (Object.keys(CATEGORY_LABEL) as CommentCategory[])
    .map(c => ({ c, n: data.totals.byCategory[c] ?? 0 }))
    .filter(x => x.n > 0)
    .sort((a, b) => b.n - a.n)
  const maxCategory = categories[0]?.n ?? 0
  const platformTotal =
    data.byPlatform.FACEBOOK.hidden +
    data.byPlatform.FACEBOOK.kept +
    data.byPlatform.INSTAGRAM.hidden +
    data.byPlatform.INSTAGRAM.kept
  const judgeTotal = Object.values(data.byJudge).reduce(
    (a, b) => a + (b ?? 0),
    0,
  )
  const hiddenTotal =
    data.totals.hidden + data.totals.unhidden + data.totals.hideFailed
  const hiddenShare =
    data.totals.total > 0
      ? Math.round((hiddenTotal / data.totals.total) * 100)
      : 0

  return (
    <div className="grid grid-cols-1 gap-system-md lg:grid-cols-3">
      <div className="lg:col-span-2">
        <Panel
          title="Comments judged per day"
          hint={`${hiddenShare}% of judged comments were hidden`}
        >
          {days.length === 0 ? (
            <Empty text="Nothing judged in this window." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={days}
                margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                barCategoryGap="30%"
              >
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDay}
                  tick={{ fill: MUTED, fontSize: 11 }}
                  axisLine={{ stroke: GRID }}
                  tickLine={false}
                  minTickGap={16}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: MUTED, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{
                    background: "#101028",
                    border: `1px solid ${GRID}`,
                    borderRadius: 8,
                    fontSize: 12,
                    color: INK,
                  }}
                  labelStyle={{ color: MUTED }}
                  labelFormatter={value => shortDay(String(value))}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: MUTED }}
                  iconType="circle"
                  iconSize={8}
                />
                {SERIES.map((s, i) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    name={s.label}
                    stackId="judged"
                    fill={s.color}
                    stroke="#101028"
                    strokeWidth={1}
                    radius={i === SERIES.length - 1 ? [4, 4, 0, 0] : 0}
                    maxBarSize={36}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <Panel title="Why comments were hidden or kept">
        {categories.length === 0 ? (
          <Empty text="No decisions yet." />
        ) : (
          <ul className="flex flex-col gap-2">
            {categories.map(({ c, n }) => (
              <BarRow
                key={c}
                label={CATEGORY_LABEL[c]}
                value={n}
                max={maxCategory}
                color={CATEGORY_COLOR[c]}
              />
            ))}
          </ul>
        )}
        <dl className="mt-4 grid grid-cols-2 gap-2 border-t border-white/10 pt-3 text-xs">
          <div>
            <dt className="text-system-gray-500">Facebook</dt>
            <dd className="text-system-gray-100">
              {data.byPlatform.FACEBOOK.hidden} hidden ·{" "}
              {data.byPlatform.FACEBOOK.kept} kept
              {platformTotal > 0 && (
                <span className="text-system-gray-500">
                  {" "}
                  (
                  {Math.round(
                    ((data.byPlatform.FACEBOOK.hidden +
                      data.byPlatform.FACEBOOK.kept) /
                      platformTotal) *
                      100,
                  )}
                  %)
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-system-gray-500">Instagram</dt>
            <dd className="text-system-gray-100">
              {data.byPlatform.INSTAGRAM.hidden} hidden ·{" "}
              {data.byPlatform.INSTAGRAM.kept} kept
            </dd>
          </div>
          <div>
            <dt className="text-system-gray-500">Decided by</dt>
            <dd className="text-system-gray-100">
              {(["RULE", "AI", "USER"] as const)
                .filter(j => (data.byJudge[j] ?? 0) > 0)
                .map(j => `${data.byJudge[j]} ${JUDGE_LABEL[j]}`)
                .join(" · ") || "—"}
              {judgeTotal > 0 && data.aiHideConfidence != null && (
                <span className="text-system-gray-500">
                  {" "}
                  · AI hides avg {Math.round(data.aiHideConfidence * 100)}% sure
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-system-gray-500">Hide refused</dt>
            <dd className="text-system-gray-100">{data.totals.hideFailed}</dd>
          </div>
        </dl>
      </Panel>

      <Panel
        title="Terms that fired"
        hint="competitor and spam rules, by hidden comments"
      >
        {data.matchedTerms.length === 0 ? (
          <Empty text="No rule has fired in this window." />
        ) : (
          <ul className="flex flex-col gap-2">
            {data.matchedTerms.map(t => (
              <BarRow
                key={t.term}
                label={t.term}
                value={t.count}
                max={data.matchedTerms[0].count}
                color={SERIES_COLORS[4]}
              />
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Posts drawing the most hidden comments">
        {data.topPosts.length === 0 ? (
          <Empty text="Nothing hidden in this window." />
        ) : (
          <ul className="flex flex-col gap-2">
            {data.topPosts.map(p => (
              <BarRow
                key={`${p.platform}:${p.postId}`}
                label={`${p.platform === "FACEBOOK" ? "FB" : "IG"} ${p.postId}`}
                sub={`${p.adIds.length} ad${p.adIds.length === 1 ? "" : "s"} · ${p.kept} kept`}
                value={p.hidden}
                max={data.topPosts[0].hidden}
                color={SERIES_COLORS[1]}
                href={p.permalinkUrl}
              />
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Repeat authors" hint="two or more hidden comments">
        {data.repeatAuthors.length === 0 ? (
          <Empty text="No author was hidden more than once." />
        ) : (
          <ul className="flex flex-col gap-2">
            {data.repeatAuthors.map(a => (
              <BarRow
                key={a.authorId ?? a.authorName}
                label={a.authorName}
                sub={a.categories.map(c => CATEGORY_LABEL[c] ?? c).join(", ")}
                value={a.hidden}
                max={data.repeatAuthors[0].hidden}
                color={SERIES_COLORS[3]}
              />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}
